import { Box, Card, Typography } from '@mui/material';
import { useGetTransactionRequestsList } from '@/hooks/requests/useTransactionRequests';
import {
  TransactionRequestItem,
  TRANSACTION_REQUEST_STATUS,
} from '@/types/transactionRequests';
import { formatDateTimeFromUTC, getRemainingTime } from '@/utils/date';
import {
  getTransactionAmountColorWithStatus,
  getTransactionRequestStatus,
} from '@/utils/transactionRequests';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { SVButton, SVLoader, SVTabs } from '@/components/core';

type TransactionRequestTabPanelProps = {
  transactionRequests?: TransactionRequestItem[];
};

function TransactionRequestTabPanel({
  transactionRequests,
}: TransactionRequestTabPanelProps) {
  const navigate = useNavigate();
  const [viewMore, setViewMore] = useState(false);

  if (!transactionRequests || transactionRequests?.length <= 0) {
    return (
      <Typography>
        There are no transaction requests under this current status
      </Typography>
    );
  }
  const trimmedTransactionRequests = viewMore
    ? transactionRequests
    : transactionRequests.slice(0, 5);

  return (
    <>
      {trimmedTransactionRequests.map(transactionRequest => {
        const {
          uuid,
          owner,
          displayAmount,
          description,
          status,
          requestedBy,
          expirationAt,
          canceledAt,
          declinedAt,
          approvedAt,
        } = transactionRequest;
        const { statusLabel, color } = getTransactionRequestStatus(status);

        const textDecoration =
          status === TRANSACTION_REQUEST_STATUS.Canceled ||
          status === TRANSACTION_REQUEST_STATUS.Expired ||
          status === TRANSACTION_REQUEST_STATUS.Declined
            ? 'line-through'
            : 'none';

        const statusDate = TRANSACTION_REQUEST_STATUS.Approved
          ? approvedAt
          : TRANSACTION_REQUEST_STATUS.Canceled
            ? canceledAt
            : TRANSACTION_REQUEST_STATUS.Declined
              ? declinedAt
              : expirationAt;

        return (
          <Box
            key={uuid}
            sx={{
              marginBottom: '12px',
            }}>
            <Card variant="hover" onClick={() => navigate('')}>
              <Box
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {owner.userName}
                  </Typography>
                  <Typography variant="body2" color="grey">
                    {description}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    color:
                      getTransactionAmountColorWithStatus(transactionRequest),
                    textDecoration,
                  }}>
                  {displayAmount}
                </Typography>
              </Box>
            </Card>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                color: 'grey',
                gap: '5px',
                marginLeft: '5px',
              }}>
              {status === TRANSACTION_REQUEST_STATUS.Pending ? (
                <>
                  <Typography variant="body2">By</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                    }}>
                    {requestedBy.userName}
                  </Typography>
                  <Typography variant="body2" sx={{ marginLeft: '5px' }}>
                    Expires in
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                    }}>
                    {getRemainingTime(expirationAt)}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color,
                      fontWeight: 'bold',
                    }}>
                    {statusLabel}
                  </Typography>
                  {statusDate && (
                    <Typography variant="body2">
                      on {formatDateTimeFromUTC(statusDate)}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>
        );
      })}
      <SVButton variant="text" onClick={() => setViewMore(!viewMore)}>
        {viewMore ? 'View Less' : 'View More'}
      </SVButton>
    </>
  );
}

export default function AdvisorDashboardRecentTransactionRequests() {
  const {
    transactionRequests: needsApprovalTransactions,
    isTransactionRequestsListPending: needsApprovalIsLoading,
  } = useGetTransactionRequestsList({
    status: TRANSACTION_REQUEST_STATUS.Pending,
    limit: 10,
  });
  const {
    transactionRequests: underReviewTransactions,
    isTransactionRequestsListPending: underReviewIsLoading,
  } = useGetTransactionRequestsList({
    status: TRANSACTION_REQUEST_STATUS.UnderReview,
    limit: 10,
  });
  const {
    transactionRequests: historyTransactions,
    isTransactionRequestsListPending: historyIsLoading,
  } = useGetTransactionRequestsList({
    status: [
      TRANSACTION_REQUEST_STATUS.Approved,
      TRANSACTION_REQUEST_STATUS.Declined,
      TRANSACTION_REQUEST_STATUS.Canceled,
      TRANSACTION_REQUEST_STATUS.Expired,
    ],
    limit: 10,
  });

  if (historyIsLoading || needsApprovalIsLoading || underReviewIsLoading) {
    return (
      <Card>
        <SVLoader />
      </Card>
    );
  }

  const headers = ['History'];
  const panels = [
    <TransactionRequestTabPanel
      key="history-panel"
      transactionRequests={historyTransactions}
    />,
  ];
  if (needsApprovalTransactions?.length) {
    headers.unshift('Needs Approval');
    panels.unshift(
      <TransactionRequestTabPanel
        key="needs-approval-panel"
        transactionRequests={needsApprovalTransactions}
      />,
    );
  }
  if (underReviewTransactions?.length) {
    headers.unshift('Under Review');
    panels.unshift(
      <TransactionRequestTabPanel
        key="needs-approval-panel"
        transactionRequests={underReviewTransactions}
      />,
    );
  }

  return (
    <Card>
      <Typography variant="h5" mb={2}>
        Recent Transaction Requests
      </Typography>
      <SVTabs headers={headers} panels={panels} />
    </Card>
  );
}
